import { cn } from '@/ui/utils';
import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area';

interface Props extends React.ComponentProps<typeof ScrollAreaPrimitive.Root> {
  orientation?: 'horizontal' | 'vertical' | 'both';
  viewportClassName?: string;
  viewportRef?: React.Ref<HTMLDivElement>;
  onViewportScroll?: React.UIEventHandler<HTMLDivElement>;
  scrollbarGutter?: boolean;
  overscrollContain?: boolean;
  scrollbar?: boolean;
}

function ScrollArea({
  className,
  viewportClassName = '',
  viewportRef,
  onViewportScroll,
  children,
  orientation,
  scrollbarGutter = false,
  overscrollContain = true,
  scrollbar = true,
  ...props
}: Props) {
  return (
    <ScrollAreaPrimitive.Root className={cn('grow min-h-0', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        onScroll={onViewportScroll}
        className={cn(
          'size-full rounded-[inherit] outline-hidden',
          overscrollContain && 'overscroll-contain',
          scrollbarGutter && 'data-has-overflow-y:pe-2 data-has-overflow-x:pb-2',
          viewportClassName,
        )}
        data-slot="scroll-area-viewport"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {!scrollbar ? null : orientation === 'both' ? (
        <>
          <ScrollBar orientation="vertical" />
          <ScrollBar orientation="horizontal" />
        </>
      ) : (
        <ScrollBar orientation={orientation} />
      )}
      <ScrollAreaPrimitive.Corner data-slot="scroll-area-corner" />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: ScrollAreaPrimitive.Scrollbar.Props) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      className={cn(
        'm-0.5 flex opacity-0 transition-opacity delay-300 data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5 data-[orientation=horizontal]:flex-col data-hovering:opacity-100 data-scrolling:opacity-100 data-hovering:delay-0 data-scrolling:delay-0 data-hovering:duration-100 data-scrolling:duration-100',
        className,
      )}
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        className="relative flex-1 rounded-full bg-surface-6 in-data-[slot=scroll-spy-viewport]:z-25 in-[div:has(table)]:z-25"
        data-slot="scroll-area-thumb"
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}

export { ScrollArea, ScrollBar };
